// ============================================
// product-detail.js — 產品詳情頁互動（縮圖切換主圖）
// ============================================
// 漸進增強：主圖與縮圖已在 HTML 靜態輸出，無 JS 也能瀏覽。
// 本檔只負責「點縮圖 → 換主圖 + 標記 active 縮圖」，並提供 cleanup 解除 listener。
//
// 由 src/main.js 中央 import 鏈載入（root 守門早退，非該頁不執行）。
// 不靠頁面自帶 <script>：vite-plugin-html 每頁只重注入單一 entry，會剝掉手寫 script。
// ============================================

const MAIN_SELECTOR = '.product-gallery__main-img';
const THUMB_SELECTOR = '.product-gallery__thumb';
const ACTIVE_CLASS = 'is-active';

function initProductGallery() {
  const mainImg = document.querySelector(MAIN_SELECTOR);
  const thumbs = Array.from(document.querySelectorAll(THUMB_SELECTOR));
  if (!mainImg || thumbs.length === 0) return null;

  // 主圖的 webp <source>：<picture> 下瀏覽器支援 webp 時採用 source 而非 <img src>，
  // 故換主圖必須同步它的 srcset，否則大圖永遠卡在初始 webp（縮圖外框有反應、大圖不動）。
  const mainWebpSource = mainImg.closest('picture')?.querySelector('source[type="image/webp"]') || null;

  const handleThumbActivate = (thumb) => {
    const btnImg = thumb.querySelector('img');
    if (!btnImg) return;

    // 換主圖：用縮圖自帶的全尺寸來源（data-full）優先，否則退回縮圖 src
    const fullSrc = thumb.dataset.full || btnImg.currentSrc || btnImg.src;
    if (mainImg.getAttribute('src') === fullSrc) return; // 已是當前主圖，跳過

    mainImg.setAttribute('src', fullSrc);
    mainImg.setAttribute('alt', btnImg.getAttribute('alt') || mainImg.getAttribute('alt') || '');

    // 同步主圖 webp source 的 srcset：優先讀被點縮圖自己的 webp source（與 markup 真相源一致），
    // 該縮圖無 webp source 時才由 fullSrc 推導（.jpg → .webp）。兩者皆無則移除 source，
    // 讓 <img src> 接手，避免殘留舊 webp 蓋過新主圖。
    if (mainWebpSource) {
      const thumbWebp = thumb.querySelector('source[type="image/webp"]');
      const nextSrcset = thumbWebp?.getAttribute('srcset') || fullSrc.replace(/\.jpe?g$/i, '.webp');
      if (nextSrcset) {
        mainWebpSource.setAttribute('srcset', nextSrcset);
      } else {
        mainWebpSource.removeAttribute('srcset');
      }
    }

    // 標記 active 縮圖（aria-pressed + class）
    thumbs.forEach((t) => {
      const isCurrent = t === thumb;
      t.classList.toggle(ACTIVE_CLASS, isCurrent);
      t.setAttribute('aria-pressed', String(isCurrent));
    });
  };

  // 事件委派：單一 listener 掛在容器，省去逐縮圖綁定
  const strip = thumbs[0].closest('.product-gallery__thumbs') || document;

  const onClick = (e) => {
    const thumb = e.target.closest(THUMB_SELECTOR);
    if (thumb && strip.contains(thumb)) handleThumbActivate(thumb);
  };

  strip.addEventListener('click', onClick);

  // cleanup：解除唯一 listener
  return () => {
    strip.removeEventListener('click', onClick);
  };
}

// ── 麵包屑「分類」層：依 ?from=<categoryId> 回溯來源分類 ─────────────────────────
// 產品卡以 ?from=<id> 帶入來源分類（見 _products-group.html）。麵包屑分類層預設硬寫
// 「填充物 / #ha」；這裡依 from 改寫其 label 與 hash，未帶或未知分類則保留預設（優雅退回）。
// 路徑不重建：只換既有連結的 hash、保留其 path（dev '/products'、prod postprocess 後
// 'products.html'），天然 base-aware —— 子目錄部署不會被改成 root-絕對而 404。
const BREADCRUMB_CATEGORY_LABELS = {
  ha: '玻尿酸',
  botulinum: '肉毒',
  collagen: '膠原蛋白增生劑',
  laser: '雷射能量',
  lipolysis: '溶脂體雕',
  recovery: '術後修復',
  regenerative: '再生醫學',
};

function applyBreadcrumbCategory() {
  const link = document.querySelector('[data-breadcrumb-category]');
  if (!link) return; // 非本頁 / 無此麵包屑層 → 早退

  const from = new URLSearchParams(location.search).get('from');
  const label = from && BREADCRUMB_CATEGORY_LABELS[from];
  if (!label) return; // 缺漏或未知分類 → 保留硬寫預設（填充物 / #ha）

  const basePath = (link.getAttribute('href') || '').split('#')[0];
  link.setAttribute('href', `${basePath}#${from}`);
  link.textContent = label;
}

let teardown = null;

function boot() {
  teardown = initProductGallery();
  applyBreadcrumbCategory();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// HMR / SPA 離開時解除 listener，避免重複綁定
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (typeof teardown === 'function') teardown();
    teardown = null;
  });
}
